<template>
  <van-popup
    v-model="show"
    position="bottom"
    round
    safe-area-inset-bottom
    class="selection-popup"
  >
    <div class="selection-popup-header">
      <div class="selection-popup-title">选择年级时间</div>
      <div class="selection-popup-close" @click="close">×</div>
    </div>
    <div class="selection-popup-content">
      <div class="selection-popup-block">
        <div class="selection-popup-label">选择校内年级</div>
        <div class="selection-popup-options">
          <div
            v-for="item in gradeOptionList"
            :key="item.id"
            :class="[
              'selection-popup-option',
              { 'selection-popup-option-active': selectedGrade === item.id },
            ]"
            @click="handleGradeChange(item.id)"
          >
            {{ item.name }}
          </div>
        </div>
      </div>
      <div class="selection-popup-block">
        <div class="selection-popup-label">选择上课时间</div>
        <div class="selection-popup-options">
          <div
            v-for="item in timeOptionList"
            :key="item.id"
            :class="[
              'selection-popup-option',
              { 'selection-popup-option-active': selectedTime === item.id },
            ]"
            @click="handleTimeChange(item.id)"
          >
            {{ item.name }}
          </div>
        </div>
      </div>
      <div
        :class="
          canConfirm
            ? 'selection-popup-confirm'
            : 'selection-popup-confirm selection-popup-confirm-disabled'
        "
        @click="confirm"
      >
        确定
      </div>
    </div>
  </van-popup>
</template>

<script>
import { Popup } from "vant";
import { contCourseSelect } from "../../../api/api";
import { appendStuCourseIds } from "@/common/script/log-helper";

export default {
  components: {
    "van-popup": Popup,
  },
  props: {
    info: {
      type: Object,
      default: () => {
        return {};
      },
    },
  },
  data() {
    return {
      show: false,
      selectedGrade: "",
      selectedTime: "",
      optionClickLocked: false,
      optionClickLockTimer: null,
      fetchRequestId: 0,
      // 首次接口返回的可选项全集，后续始终用它来渲染筛选项
      initialAbleChoose: {
        grade_id: [],
        class_time: [],
      },
      contCourseSelectData: {
        able_choose: {
          grade_id: [],
          class_time: [],
        },
      },
    };
  },
  computed: {
    gradeOptionList() {
      return this.initialAbleChoose.grade_id || [];
    },
    timeOptionList() {
      return this.initialAbleChoose.class_time || [];
    },
    canConfirm() {
      return !!this.selectedGrade && !!this.selectedTime;
    },
  },
  methods: {
    async open() {
      this.selectedGrade = "";
      this.selectedTime = "";
      this.show = false;
      this.clearOptionClickLock();
      this.initialAbleChoose = {
        grade_id: [],
        class_time: [],
      };
      this.contCourseSelectData = {
        able_choose: {
          grade_id: [],
          class_time: [],
        },
      };
      const data = await this.fetchContCourseSelect();
      if (!this.hasFilterOptions(data?.able_choose)) {
        this.showEmptyFilterToast();
        return;
      }
      this.show = true;
    },
    close() {
      this.show = false;
    },
    clearOptionClickLock() {
      this.optionClickLocked = false;
      if (this.optionClickLockTimer) {
        clearTimeout(this.optionClickLockTimer);
        this.optionClickLockTimer = null;
      }
    },
    startOptionClickLock() {
      this.clearOptionClickLock();
      this.optionClickLocked = true;
      this.optionClickLockTimer = setTimeout(() => {
        this.optionClickLocked = false;
        this.optionClickLockTimer = null;
      }, 600);
    },
    showNoCourseToast() {
      this.$toast("该时段未匹配到课程,可换个选项试试~");
    },
    showEmptyFilterToast() {
      this.$toast("系统出错啦！建议退出重进刷新试一试哦～");
    },
    normalizeList(list) {
      return Array.isArray(list) ? list : list ? [list] : [];
    },
    hasFilterOptions(ableChoose) {
      const gradeList = this.normalizeList(ableChoose?.grade_id);
      const timeList = this.normalizeList(ableChoose?.class_time);
      return gradeList.length > 0 && timeList.length > 0;
    },
    // 以 status.id === 1 作为“有课可选”的判断标准
    getAvailableCourse(list) {
      return this.normalizeList(list).find((item) => +item?.status?.id === 1);
    },
    hasAvailableCourse(list) {
      return !!this.getAvailableCourse(list);
    },
    async fetchContCourseSelect() {
      if (!this.info?.learn_course_id) {
        return;
      }
      const requestId = ++this.fetchRequestId;
      try {
        const { data } = await contCourseSelect({
          learn_course_id: this.info.learn_course_id,
          is_mixed_goods: this.info.is_mixed_goods ? 1 : 0,
          grade_id: this.selectedGrade,
          class_time: this.selectedTime,
          learn_stu_course_id: this.info.learn_stu_course_id,
        });
        if (requestId !== this.fetchRequestId) {
          return;
        }
        const nextData = data || {
          able_choose: {
            grade_id: [],
            class_time: [],
          },
        };
        this.contCourseSelectData = nextData;
        if (
          this.initialAbleChoose.grade_id.length === 0 &&
          this.initialAbleChoose.class_time.length === 0
        ) {
          // 只在首次打开时记录一份全集，后续点击筛选项不再改动它
          this.initialAbleChoose = {
            grade_id: (
              this.contCourseSelectData?.able_choose?.grade_id || []
            ).map((item) => ({ ...item })),
            class_time: (
              this.contCourseSelectData?.able_choose?.class_time || []
            ).map((item) => ({ ...item })),
          };
        }
        return nextData;
      } catch (error) {
        console.error("contCourseSelect 请求失败", error);
        return null;
      }
    },
    async handleGradeChange(gradeId) {
      if (this.optionClickLocked) {
        return;
      }
      this.startOptionClickLock();
      const nextGrade = this.selectedGrade === gradeId ? "" : gradeId;
      this.selectedGrade = nextGrade;
      // 年级被反选取消时，上课时间也一起清空
      //if (!nextGrade) {
      //this.selectedTime = "";
      //}
      const data = await this.fetchContCourseSelect();
      if (data && !this.hasAvailableCourse(data.list)) {
        this.showNoCourseToast();
      }
    },
    async handleTimeChange(timeId) {
      if (this.optionClickLocked) {
        return;
      }
      this.startOptionClickLock();
      this.selectedTime = this.selectedTime === timeId ? "" : timeId;
      const data = await this.fetchContCourseSelect();
      if (data && !this.hasAvailableCourse(data.list)) {
        this.showNoCourseToast();
      }
    },
    async confirm() {
      if (!this.canConfirm) {
        return;
      }
      const data = await this.fetchContCourseSelect();
      // 确定前再兜底校验一次，没课时直接拦住，不关闭弹窗
      const matchedCourse = this.getAvailableCourse(data?.list);
      if (!matchedCourse) {
        this.showNoCourseToast();
        return;
      }
      this.$xesLogger.clickMsg({
        params: appendStuCourseIds({
          click_id: "click_D7qLm2Xa",
          click_name: "选择年级时间确定点击",
          grade_id: this.selectedGrade,
          class_time: this.selectedTime,
          list: [matchedCourse], // 匹配到的课程
          click_timestamp: Date.now(), // 点击时间
        }),
      });
      this.$emit("confirm", {
        grade: this.selectedGrade,
        time: this.selectedTime,
        list: [matchedCourse],
      });
      this.close();
    },
  },
  beforeDestroy() {
    this.clearOptionClickLock();
  },
};
</script>

<style lang="less" scoped>
@import "../../../assets/less/layout.less";

.selection-popup {
  overflow: hidden;
}
.selection-popup-header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 52px;
  background: #fff;
}
.selection-popup-title {
  color: #333333;
  font-family: PingFang SC;
  font-size: 17px;
  font-weight: 500;
  line-height: 24px;
}
.selection-popup-close {
  position: absolute;
  top: 50%;
  right: 18px;
  transform: translateY(-50%);
  color: #333;
  font-size: 28px;
  // line-height: 28px;
}
.selection-popup-content {
  padding: 12px 15px 9px 15px;
  background: #fff;
}
.selection-popup-block + .selection-popup-block {
  margin-top: 9px;
}
.selection-popup-label {
  color: #333333;
  font-family: PingFang SC;
  font-size: 15px;
  font-weight: 500;
  line-height: 21px;
}
.selection-popup-options {
  display: flex;
  flex-wrap: wrap;
  margin-top: 16px;
  line-height: 40px;
}
.selection-popup-option {
  .flex-center-center;
  width: calc((100% - 24px) / 3);
  height: 40px;
  margin-right: 12px;
  margin-bottom: 15px;
  border-radius: 20px;
  background: #f6f6f6;
  color: #333;
  font-family: PingFang SC;
  font-size: 13px;
  font-weight: 400;
  // line-height: 21px;
  box-sizing: border-box;
}
.selection-popup-option:nth-child(3n) {
  margin-right: 0;
}
.selection-popup-option-active {
  background: rgba(249, 56, 52, 0.12);
  border: 1px solid #f93834;
  color: #f93834;
  font-weight: 500;
}
.selection-popup-confirm {
  .flex-center-center;
  height: 44px;
  margin-top: 8px;
  border-radius: 28px;
  background: #f93834;
  color: #fff;
  font-family: PingFang SC;
  font-size: 15px;
  font-weight: 500;
  line-height: 21px;
}
.selection-popup-confirm-disabled {
  background: #9999991a;
  color: #adadad;
  pointer-events: none;
}
</style>
